/**
 * NEXORA AI - Enterprise API Wrapper
 * 
 * Système centralisé de gestion de session avec auto-refresh transparent.
 * Toutes les requêtes API doivent utiliser fetchWithAuth() au lieu de fetch().
 * 
 * Fonctionnalités :
 * - Auto-refresh du token quand il expire (401)
 * - Retry automatique de la requête après refresh
 * - Redirection vers login si le refresh échoue
 * - Gestion centralisée des erreurs
 * 
 * Sprint C.15 - Bug C.15-AI-001
 * NEXORA Engineering Method : "Une architecture globale évite qu'un bug réapparaisse ailleurs."
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Flag pour éviter les refresh loops infinis
 */
let isRefreshing = false;
let refreshPromise = null;

/**
 * Refresh l'access token en utilisant le refresh token (cookie httpOnly)
 * 
 * @returns {Promise<boolean>} True si le refresh a réussi, false sinon
 */
async function refreshAccessToken() {
  // Si un refresh est déjà en cours, attendre le résultat
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // Envoie le refresh_token cookie
      });

      if (response.ok) {
        // Le backend a créé un nouveau cookie access_token
        console.log('✅ Access token refreshed successfully');
        return true;
      } else {
        console.warn('❌ Refresh token expired or invalid');
        return false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Wrapper fetch avec gestion automatique du token refresh
 * 
 * Utilisation identique à fetch() :
 * ```javascript
 * const response = await fetchWithAuth('/api/ai-studio/profiles', {
 *   method: 'POST',
 *   body: JSON.stringify(data)
 * });
 * ```
 * 
 * @param {string} url - URL de l'API (absolue ou relative)
 * @param {RequestInit} options - Options fetch (méthode, headers, body, etc.)
 * @returns {Promise<Response>} - Réponse de l'API
 * @throws {Error} - Si la session a expiré et nécessite une reconnexion
 */
export async function fetchWithAuth(url, options = {}) {
  // Construire l'URL complète si nécessaire
  const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;

  // Ajouter credentials: 'include' par défaut
  const requestOptions = {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  // Première tentative
  let response = await fetch(fullUrl, requestOptions);

  // Si 401, tenter un refresh puis retry
  if (response.status === 401) {
    console.log('🔄 Token expired, attempting refresh...');

    const refreshed = await refreshAccessToken();

    if (refreshed) {
      // Retry la requête avec le nouveau token
      console.log('🔄 Retrying original request with new token...');
      response = await fetch(fullUrl, requestOptions);
    } else {
      // Refresh échoué → Session expirée
      console.error('❌ Session expired - redirecting to login');
      
      // Redirection vers login avec message
      const currentPath = window.location.pathname;
      window.location.href = `/login?expired=true&redirect=${encodeURIComponent(currentPath)}`;
      
      throw new Error('Session expirée. Merci de vous reconnecter.');
    }
  }

  return response;
}

/**
 * Helper pour les requêtes GET
 */
export async function getWithAuth(url) {
  return fetchWithAuth(url, { method: 'GET' });
}

/**
 * Helper pour les requêtes POST
 */
export async function postWithAuth(url, data) {
  return fetchWithAuth(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Helper pour les requêtes PATCH
 */
export async function patchWithAuth(url, data) {
  return fetchWithAuth(url, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Helper pour les requêtes DELETE
 */
export async function deleteWithAuth(url) {
  return fetchWithAuth(url, { method: 'DELETE' });
}

export default fetchWithAuth;
